/// <amd-module name="Wit/DeliveryPlans/Board/Graph/Edge" />
import * as React from "react";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { css } from "VSSUI/Util";
import { Constants, IPoint } from "./Contracts";
import { PathBuilder } from "./PathBuilder";

export interface IEdgeProps extends IVssComponentProperties {
    /**
     * render as an arc rather than a straight line
     */
    arc?: boolean;
    className?: string;
    from: IPoint;
    to: IPoint;
    intermediate1?: IPoint;
    intermediate2?: IPoint;
    isInvalid?: boolean;
    /**
     * Should the line point at or away from the origin point
     */
    isSourcePredecessor: boolean;
    onClick?: (event: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
    /**
     * Indicates the vertical displacement of quadraticBezier
     * in edge
     */
    quadraticBezierVerticalDisplacement?: number;
    quadraticBezierHorizontalDisplacement?: number;
    edgeAriaLabel?: string;
}

export const focusableEdgeElementClass = "focusable-edge-wrapper";

export class Edge extends VssComponent<IEdgeProps, {}> {
    private midline: string | undefined;

    public render(): JSX.Element {
        const { className, from, to, onClick, isInvalid, isSourcePredecessor, edgeAriaLabel } = this.props;
        let pathAttribute = "";
        const intermediate1 = this.props.intermediate1;
        const intermediate2 = this.props.intermediate2;

        const pathBuilder = new PathBuilder().start(from);
        if (intermediate1 !== undefined && intermediate2 !== undefined) {
            this._addEdgeSegment(pathBuilder, from, intermediate1);
            this._addEdgeSegment(pathBuilder, intermediate1, intermediate2);
            this._addEdgeSegment(pathBuilder, intermediate2, to);
        } else {
            this._addEdgeSegment(pathBuilder, from, to);
        }

        pathAttribute = pathBuilder.toString();

        const marker = isInvalid ? "invalid-arrow" : "arrow";
        return (
            <a aria-label={edgeAriaLabel} className={focusableEdgeElementClass} href="#">
                <path
                    className={css("delivery-plans-canvas-path ", className, isInvalid && "invalid-dependency")}
                    color="currentColor"
                    cursor={onClick ? "pointer" : undefined}
                    d={pathAttribute}
                    markerEnd={!isSourcePredecessor ? `url(#${marker})` : undefined}
                    markerStart={isSourcePredecessor ? `url(#${marker})` : undefined}
                    onClick={onClick}
                />
                {/** insert a simple path that we can use to place an icon in the middle of the line */}
                {isInvalid && this.midline && (
                    <path
                        className={css("delivery-plans-canvas-path ", className, isInvalid && "invalid-dependency")}
                        color="currentColor"
                        cursor={onClick ? "pointer" : undefined}
                        d={this.midline}
                        markerMid="url(#invalid-icon)"
                        onClick={onClick}
                    />
                )}
            </a>
        );
    }

    private _addEdgeSegment(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        if (from.y === to.y || from.x === to.x) {
            this._addStraightLine(pathBuilder, from, to);
        } else if (from.y > to.y) {
            this._addUpwardCurve(pathBuilder, from, to);
        } else {
            this._addDownwardCurve(pathBuilder, from, to);
        }
    }

    private _addStraightLine(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        pathBuilder.line(to);

        // add a midline so we can place the error indicator on the line.
        // note that while this isn't strictly necessary when we have a straight line,
        // it felt best to maintain consistency with how the error indicator is placed in this component
        if (this.props.isInvalid) {
            const midpoint = {
                x: from.x === to.x ? from.x : Math.min(to.x, from.x) + Math.abs(to.x - from.x) / 2,
                y: from.y === to.y ? from.y : Math.min(to.y, from.y) + Math.abs(to.y - from.y) / 2
            };
            this.midline = new PathBuilder().start(from).line(midpoint).line(to).toString();
        }
    }

    private _addUpwardCurve(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        const horizontalDistance = from.x < to.x ? to.x - from.x : from.x - to.x;
        const normalizedDistance = Math.min(horizontalDistance / 2, horizontalDistance);
        let intermediatePointX = from.x < to.x ? from.x + normalizedDistance : from.x - normalizedDistance;
        if (this.props.quadraticBezierHorizontalDisplacement) {
            intermediatePointX += this.props.quadraticBezierHorizontalDisplacement;
        }

        pathBuilder
            .quadraticBezier(
                {
                    x: intermediatePointX,
                    y: from.y - this._getQuadraticBezierVerticalDisplacement()
                },
                {
                    x: intermediatePointX,
                    y: from.y
                }
            )
            .line({ x: intermediatePointX, y: to.y + this._getQuadraticBezierVerticalDisplacement() }) // Vertical line at the center
            .quadraticBezier(
                {
                    x: to.x,
                    y: to.y
                },
                {
                    x: intermediatePointX,
                    y: to.y
                }
            );

        // save off a string that copies the line that runs between the quadratic beziers
        if (this.props.isInvalid) {
            this.midline = this._getMidline(
                intermediatePointX,
                to.y + this._getQuadraticBezierVerticalDisplacement(),
                from.y - this._getQuadraticBezierVerticalDisplacement()
            );
        }
    }

    private _addDownwardCurve(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        const horizontalDistance = from.x < to.x ? to.x - from.x : from.x - to.x;
        const normalizedDistance = Math.min(horizontalDistance / 2, horizontalDistance);
        let intermediatePointX = from.x < to.x ? from.x + normalizedDistance : from.x - normalizedDistance;
        if (this.props.quadraticBezierHorizontalDisplacement) {
            intermediatePointX += this.props.quadraticBezierHorizontalDisplacement;
        }

        pathBuilder
            .quadraticBezier(
                {
                    x: intermediatePointX,
                    y: from.y + this._getQuadraticBezierVerticalDisplacement()
                },
                {
                    x: intermediatePointX,
                    y: from.y
                }
            )
            .line({ x: intermediatePointX, y: to.y - this._getQuadraticBezierVerticalDisplacement() }) // Vertical line at the center
            .quadraticBezier(
                {
                    x: to.x,
                    y: to.y
                },
                {
                    x: intermediatePointX,
                    y: to.y
                }
            );

        // save off a string that copies the line that runs between the quadratic beziers
        if (this.props.isInvalid) {
            this.midline = this._getMidline(
                intermediatePointX,
                from.y + this._getQuadraticBezierVerticalDisplacement(),
                to.y - this._getQuadraticBezierVerticalDisplacement()
            );
        }
    }

    private _addArc(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        const radius = Math.abs(to.x - from.x);
        pathBuilder.arc(to, radius, radius, 1, 1);
    }

    /**
     * Generate an SVG line corresponding to an x coord and a y start and y end.
     * Include a midpoint so markers can be added to the middle of the line.
     * @param x
     * @param yStart
     * @param yEnd
     */
    private _getMidline(x: number, yStart: number, yEnd: number) {
        return new PathBuilder()
            .start({
                x,
                y: yStart
            })
            .line({
                x,
                y: yStart + Math.abs(yEnd - yStart) / 2
            })
            .line({ x, y: yEnd })
            .toString();
    }

    private _getQuadraticBezierVerticalDisplacement(): number {
        if (this.props.quadraticBezierVerticalDisplacement) {
            return this.props.quadraticBezierVerticalDisplacement;
        }

        return Constants.verticalDisplacement;
    }
}
